// src/components/Repository/RepositorySpace.jsx
import React, { useEffect, useState, useCallback } from "react";
import "../../css/KnowledgeSpace.css";
import KnowledgeTitleBar from "./KnowledgeTitleBar";
import Repo_Folder from "../../assets/Images/Repo_Folder.png";
import ListIcon from "../../assets/Images/entypo_list.png";
import GridIcon from "../../assets/Images/Grid_layout.png";
import api from "../../api";
import crossIcon from "../../assets/icons/crossIcon.png";
import deleteIcon from "../../assets/Images/Delete.png";
import downloadIcon from "../../assets/Images/Download.png";
import renameIcon from "../../assets/Images/Edit.png";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { formatDateToUserTimeZone } from "../../utils/formatDate";
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  File as FileIcon,
  Plus,
  FolderSymlink,
  FileSymlink,
} from "lucide-react";

/* ---------------------------
  FolderNode component (move modal)
  - largely unchanged from your implementation
----------------------------*/
const DRAG_TYPE_SELECTED_ITEMS = "SELECTED_ITEMS_DRAG";

const FolderNode = ({
  node,
  level = 0,
  moveTargetId,
  setMoveTargetId,
  renameFolderId,
  setRenameFolderId,
  onRename,
  onDropMove,
}) => {
  const [expanded, setExpanded] = useState(node.id === "root");
  const [tempName, setTempName] = useState(node.name || "");
  const isRenaming = renameFolderId === node.id;
  const hasChildren = Object.keys(node.__folders || {}).length > 0;

  // we only need drop behaviour for move modal; simplified to click set target
  const handleClick = (e) => {
    e.stopPropagation();
    setExpanded((p) => !p);
    setMoveTargetId(node.id === "root" ? null : node.id);
    if (onDropMove) onDropMove(node);
  };

  return (
    <div style={{ paddingLeft: `${level * 14}px` }}>
      <div
        className={`folder-row ${expanded ? "open" : ""} ${
          moveTargetId === node.id ? "selected" : ""
        }`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 8px",
          borderRadius: "var(--border-radius)",
          cursor: "pointer",
          transition: "background 0.2s",
          background:
            moveTargetId === node.id
              ? "var(--selected-bg)"
              : expanded
              ? "rgba(0,0,0,0.06)"
              : "transparent",
        }}
        onClick={handleClick}
      >
        <span style={{ width: 18 }} />
        {expanded && hasChildren ? (
          <FolderOpenIcon size={18} strokeWidth={1.8} className="folder-icon" />
        ) : (
          <FolderIcon size={18} strokeWidth={1.8} className="folder-icon" />
        )}

        {isRenaming ? (
          <input
            autoFocus
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            onBlur={async () => {
              const nameToSave = tempName.trim() || "New Folder";
              await onRename(node.id, nameToSave);
              setRenameFolderId(null);
            }}
            style={{
              marginLeft: 6,
              padding: "4px 6px",
              fontSize: 14,
              minWidth: 120,
            }}
          />
        ) : (
          <span
            style={{ marginLeft: 6 }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setRenameFolderId(node.id);
            }}
          >
            {node.name}
          </span>
        )}
      </div>

      <div
        style={{
          maxHeight: expanded ? "500px" : "0px",
          overflow: "hidden",
          transition: "max-height 0.25s ease",
        }}
      >
        {Object.values(node.__folders || {}).map((child) => (
          <FolderNode
            key={child.id}
            node={child}
            level={level + 1}
            moveTargetId={moveTargetId}
            setMoveTargetId={setMoveTargetId}
            renameFolderId={renameFolderId}
            setRenameFolderId={setRenameFolderId}
            onRename={onRename}
            onDropMove={onDropMove}
          />
        ))}
      </div>
    </div>
  );
};

/* ---------------------------
  RepositorySpace (main)
----------------------------*/
const RepositorySpace = () => {
  // breadcrumbs / view state
  const [path, setPath] = useState([
    "Knowledge Repository",
    "Trained Documents",
  ]);
  const [viewMode, setViewMode] = useState("list");

  // data
  const [tree, setTree] = useState(null);
  const [flatItems, setFlatItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // modals / actions
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showMoveUI, setShowMoveUI] = useState(false);
  const [moveTargetId, setMoveTargetId] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [newName, setNewName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [renameFolderId, setRenameFolderId] = useState(null);

  // === Selection (Option B: Set-based, keys are "itemType:idOrUploadedId")
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // utility: derive file category
  const getFileCategory = (item) => {
    if (item.itemType === "folder") return "Folder";
    if (!item.name) return "Unknown";
    const name = item.name.toLowerCase();
    if (name.includes("youtube.com") || name.includes("youtu.be"))
      return "Video URL";
    if (name.startsWith("http") || name.startsWith("url:")) return "Web URL";
    if (name.endsWith(".pdf")) return "Pdf";
    if (name.endsWith(".docx") || name.endsWith(".doc")) return "Doc";
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) return "Excel";
    if (name.endsWith(".txt")) return "Text";
    if (name.endsWith(".csv")) return "Csv";
    return "Other";
  };

  const isUrlType = (f) => {
    const type = getFileCategory(f);
    return type === "Web URL" || type === "Video URL";
  };

  // fetch
  const fetchData = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/trained-documents/get-trained-docs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const items = Array.isArray(res.data) ? res.data : [];

      const categorizedItems = items.map((item) => ({
        ...item,
        name:
          item.name ||
          item.originalName ||
          item.file_name ||
          item.filename ||
          item.original_file_name ||
          item.originalFilename ||
          item.fileName ||
          item.title ||
          (item.itemType === "folder"
            ? "New Folder"
            : `File ${item.id || item.uploaded_file_id}`),
        category: getFileCategory(item),
        uploadedBy: item.uploadedBy
          ? item.uploadedBy.charAt(0).toUpperCase() + item.uploadedBy.slice(1)
          : "N/A",
      }));

      // console.log("Fetched Items:", items);

      setFlatItems(categorizedItems);
      const built = buildTree(categorizedItems);
      setTree(built);
    } catch (err) {
      console.error("❌ Error fetching trained documents", err);
      showErrorToast("Failed to fetch documents");
    }
  };

  // build tree (unchanged logic)
  const buildTree = (items = []) => {
    const root = {
      id: "root",
      name: "Trained Documents",
      itemType: "folder",
      parentId: null,
      __folders: {},
      __files: [],
    };

    if (!items || !items.length) return root;

    const folderMap = { root };

    items
      .filter((i) => i.itemType === "folder")
      .forEach((f) => {
        folderMap[f.id] = {
          id: f.id,
          name: f.name,
          itemType: "folder",
          parentId: f.parentId || null,
          __folders: {},
          __files: [],
          uploadedBy: f.uploadedBy || "N/A",
        };
      });

    Object.values(folderMap).forEach((folderNode) => {
      if (folderNode.id === "root") return;
      const parentId = folderNode.parentId || "root";
      const parent = folderMap[parentId] || root;
      parent.__folders[folderNode.id] = folderNode;
    });

    items
      .filter((i) => i.itemType === "file" || i.itemType === "uploaded")
      .forEach((file) => {
        const fileObj = {
          id: file.id,
          uploaded_file_id: file.uploaded_file_id || null,
          name: file.name,
          itemType: file.itemType,
          parentId: file.parentId || null,
          size: file.size || 0,
          status: file.status || null,
          updatedAt: file.updatedAt || null,
          pythonFileUuid: file.pythonFileUuid || null,
          trained_file_path: file.trained_file_path || file.file_path || null,
          rawUploaded: file.itemType === "uploaded",
          uploadedBy: file.uploadedBy || "N/A",
        };
        const parentId = fileObj.parentId || "root";
        const parent = folderMap[parentId] || root;
        parent.__files.push(fileObj);
      });

    return root;
  };

  // node at breadcrumb path
  const getNodeAtPath = (p = path) => {
    if (!tree) return null;
    let node = tree;
    for (let i = 2; i < p.length; i++) {
      const folderName = p[i];
      const found = Object.values(node.__folders || {}).find(
        (f) => f.name === folderName
      );
      if (!found) return node;
      node = found;
    }
    return node;
  };

  const node = getNodeAtPath(path); // current node object (may be null early)

  const navigateInto = (folderName) => setPath((prev) => [...prev, folderName]);
  const navigateToBreadcrumbIndex = (index) => {
    if (index === 0) return;
    setPath((prev) => prev.slice(0, index + 1));
  };

  const showNewButton = path[0] === "Knowledge Repository";

  const isValidFolderName = (name) => {
    const regex = /^[A-Za-z0-9 _-]+$/;
    return regex.test(name.trim());
  };

  // createFolder (unchanged)
  const createFolder = async (folderName, parentId = null) => {
    const trimmed = folderName.trim();
    if (!trimmed) return showErrorToast("Folder name cannot be empty");
    if (!isValidFolderName(trimmed))
      return showErrorToast(
        "Invalid name — Only letters, numbers, spaces, hyphens, and underscores allowed."
      );

    try {
      setIsCreatingFolder(true);
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/trained-documents/create-folder`,
        { name: trimmed, parent_id: parentId === "root" ? null : parentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSuccessToast("Folder created successfully");
      await fetchData();
      setShowFolderModal(false);
      setNewFolderName("");
      return res.data;
    } catch (err) {
      console.error("❌ createFolder error", err);
      showErrorToast(err?.response?.data?.error || "Failed to create folder");
    } finally {
      setIsCreatingFolder(false);
    }
  };

  // renameFolder (unchanged)
  const renameFolder = async (folderId, newNameParam) => {
    if (!folderId || !newNameParam) return;
    try {
      await api.put(
        `${process.env.REACT_APP_API_URL}/trained-documents/rename/${folderId}`,
        { new_name: newNameParam },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccessToast("Renamed successfully");
      setRenameFolderId(null);
      await fetchData();
    } catch (err) {
      console.error("❌ renameFolder error", err);
      showErrorToast(err?.response?.data?.error || "Rename failed");
    }
  };

  // open move UI
  const openMoveUI = () => {
    setMoveTargetId(null);
    setRenameFolderId(null);
    setShowMoveUI(true);
  };

  // helper: produce stable key for an item for our Set
  // key format: `${itemType}:${idOrUploadedId}`
  // const getItemKey = (item) => {
  //   // itemType could be 'folder', 'file', or 'uploaded'
  //   const type = item.itemType || item.type || "unknown";
  //   // prefer uploaded_file_id for uploaded items
  //   const id = item.uploaded_file_id ?? item.id ?? item._id ?? "unknown-id";
  //   return `${type}:${id}`;
  // };
  const getItemKey = (item) => {
    const type =
      item.itemType === "uploaded"
        ? "uploaded"
        : item.itemType === "folder"
        ? "folder"
        : "file";

    const id = item.itemType === "uploaded" ? item.uploaded_file_id : item.id;

    return `${type}:${id}`;
  };

  // helper to map selected Set (keys) back to item descriptors for API payloads
  const selectedKeysToPayload = () => {
    const keys = Array.from(selectedItems);
    return keys.map((k) => {
      const [type, id] = k.split(":");
      if (type === "uploaded") return { type: "uploaded", id };
      if (type === "file") return { type: "file", id };
      if (type === "folder") return { type: "folder", id };
      // fallback
      return { type: "file", id };
    });
  };

  // performMove -> uses selectedKeysToPayload
  const performMove = async () => {
    const payload = selectedKeysToPayload();
    if (!payload.length) return showErrorToast("No items selected");
    try {
      await api.put(
        `${process.env.REACT_APP_API_URL}/trained-documents/move-items`,
        {
          items: payload,
          target_folder_id: moveTargetId || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccessToast("Moved successfully");
      setSelectedItems(new Set());
      setShowMoveUI(false);
      await fetchData();
    } catch (err) {
      console.error("❌ performMove error", err);
      showErrorToast("Move failed");
    }
  };

  const handleNewFolderInMove = async () => {
    const parentId = moveTargetId || null;
    await createFolder("New Folder", parentId);
  };

  // Delete selected items (multiple)
  const doDeleteItems = async (itemsToDeleteKeys) => {
    // itemsToDeleteKeys is optional. If not provided, use selectedItems
    const keysToDelete = itemsToDeleteKeys
      ? Array.from(itemsToDeleteKeys)
      : Array.from(selectedItems);

    if (!keysToDelete.length) return showErrorToast("No items selected");

    try {
      // Detect uploaded vs trained keys
      const uploadedIds = keysToDelete
        .filter((k) => k.startsWith("uploaded:"))
        .map((k) => k.split(":")[1]);

      const trainedIds = keysToDelete
        .filter((k) => k.startsWith("file:") || k.startsWith("folder:"))
        .map((k) => k.split(":")[1]);

      if (trainedIds.length) {
        await api.delete(
          `${process.env.REACT_APP_API_URL}/trained-documents/delete-items`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: { item_ids: trainedIds },
          }
        );
      }

      await Promise.all(
        uploadedIds.map((uid) =>
          api.delete(
            `${process.env.REACT_APP_API_URL}/trained-documents/uploaded-file/${uid}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
        )
      );

      showSuccessToast("Deleted successfully");
      setSelectedItems(new Set());
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await fetchData();
    } catch (err) {
      console.error("❌ doDeleteItems error", err);
      showErrorToast("Delete failed");
    }
  };

  // handle single row delete (opens confirmation modal)
  const onRowDelete = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  // handle top-bar delete (multi)
  const onTopDelete = () => {
    if (!selectedItems.size) return;
    // show a generic confirmation modal with names? We can reuse delete modal but set deleteTarget to a synthetic object
    setDeleteTarget({ name: `${selectedItems.size} item(s)` });
    setShowDeleteModal(true);
  };

  // Download
  const handleDownload = async (item) => {
    try {
      const idToUse =
        item.itemType === "uploaded" ? item.uploaded_file_id : item.id;
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/trained-documents/download/${idToUse}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = item.name || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast("Download successful");
    } catch (err) {
      console.error("❌ handleDownload error", err);
      showErrorToast("Download failed");
    }
  };

  // rename modal (single)
  const openRenameModal = (item) => {
    setRenameTarget(item);
    setNewName(item.name || "");
    setShowRenameModal(true);
  };

  const performRename = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return showErrorToast("Name cannot be empty");
    if (!isValidFolderName(trimmed))
      return showErrorToast(
        "Invalid name — Only letters, numbers, spaces, hyphens, and underscores allowed."
      );

    setIsRenaming(true);
    try {
      await api.put(
        `${process.env.REACT_APP_API_URL}/trained-documents/rename/${renameTarget.id}`,
        { new_name: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccessToast("Renamed successfully");
      setShowRenameModal(false);
      setRenameTarget(null);
      setNewName("");
      await fetchData();
    } catch (err) {
      console.error("❌ performRename error", err);
      showErrorToast(err?.response?.data?.error || "Rename failed");
    } finally {
      setIsRenaming(false);
    }
  };

  // helpers: sizes, search filtering (kept your logic)
  const getFolderSize = (folder) => {
    if (!folder) return 0;
    let size = 0;
    if (folder.__files && folder.__files.length) {
      size += folder.__files.reduce((acc, f) => acc + (f.size || 0), 0);
    }
    Object.values(folder.__folders || {}).forEach((sub) => {
      size += getFolderSize(sub);
    });
    return size;
  };

  const formatSize = (size = 0) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filterNodeBySearch = (nodeObj, term) => {
    if (!nodeObj) return { __folders: {}, __files: [] };
    const lowerTerm = term.toLowerCase();

    const filteredFolders = {};
    Object.values(nodeObj.__folders || {}).forEach((f) => {
      const lastUpdated =
        f.__files && f.__files.length
          ? f.__files
              .map((x) => x.updatedAt || 0)
              .reduce((a, b) => Math.max(a, b), 0)
          : 0;
      const folderSize = getFolderSize(f);
      if (
        f.name.toLowerCase().includes(lowerTerm) ||
        "folder".includes(lowerTerm) ||
        (f.uploadedBy && f.uploadedBy.toLowerCase().includes(lowerTerm)) ||
        formatSize(folderSize).toLowerCase().includes(lowerTerm) ||
        (lastUpdated
          ? formatDateToUserTimeZone(f.updatedAt)
              .toLowerCase()
              .includes(lowerTerm)
          : false)
      ) {
        filteredFolders[f.id] = f;
      }
    });

    const filteredFiles = (nodeObj.__files || []).filter((f) => {
      const fileSize = formatSize(f.size);
      const fileCategory = getFileCategory(f).toLowerCase();
      return (
        f.name.toLowerCase().includes(lowerTerm) ||
        f.itemType.toLowerCase().includes(lowerTerm) ||
        fileCategory.includes(lowerTerm) ||
        (f.uploadedBy && f.uploadedBy.toLowerCase().includes(lowerTerm)) ||
        fileSize.toLowerCase().includes(lowerTerm) ||
        (f.updatedAt
          ? formatDateToUserTimeZone(f.updatedAt)
              .toLowerCase()
              .includes(lowerTerm)
          : false)
      );
    });

    return { ...nodeObj, __folders: filteredFolders, __files: filteredFiles };
  };

  const filteredNode = searchTerm ? filterNodeBySearch(node, searchTerm) : node;
  const activeNode = filteredNode; // visible node for current breadcrumb/search

  // keep selectAll state in sync
  useEffect(() => {
    if (!activeNode) {
      setSelectAll(false);
      return;
    }
    const totalVisible =
      Object.keys(activeNode.__folders || {}).length +
      (activeNode.__files || []).length;
    setSelectAll(selectedItems.size > 0 && selectedItems.size === totalVisible);
  }, [selectedItems, activeNode]);

  // toggle single folder key
  const toggleSelectFolder = (folder) => {
    const key = getItemKey({ ...folder, itemType: "folder" });
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      if (updated.has(key)) updated.delete(key);
      else updated.add(key);
      return updated;
    });
  };

  // toggle single file key
  const toggleSelectFile = (file) => {
    const key = getItemKey({ ...file, itemType: file.itemType || "file" });
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      if (updated.has(key)) updated.delete(key);
      else updated.add(key);
      return updated;
    });
  };

  // select all / deselect all (on visible activeNode)
  const handleSelectAll = (checked) => {
    if (!activeNode) return;
    const allKeys = [
      ...Object.values(activeNode.__folders || {}).map((f) =>
        getItemKey({ ...f, itemType: "folder" })
      ),
      ...(activeNode.__files || []).map((f) =>
        getItemKey({ ...f, itemType: f.itemType || "file" })
      ),
    ];
    if (checked) {
      setSelectedItems(new Set(allKeys));
    } else {
      setSelectedItems(new Set());
    }
    setSelectAll(checked);
  };

  // helper to render links in file name
  const renderWithLinks = (text) => {
    if (!text) return null;
    const cleanText = text.replace(/^(URL:|YouTube:)\s*/i, "");
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return cleanText.split(urlRegex).map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
        >
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  // render rows (list)
  const renderFolderAndFileRows = (nodeObj) => {
    if (!nodeObj) return null;
    const folders = Object.values(nodeObj.__folders || {});
    const files = nodeObj.__files || [];

    if (!folders.length && !files.length) {
      return (
        <tr>
          <td
            colSpan="7"
            style={{
              textAlign: "center",
              padding: 20,
              fontSize: "var(--ak-font-md)",
            }}
          >
            No documents have been trained yet.
          </td>
        </tr>
      );
    }

    const folderRows = folders.map((f) => {
      const lastUpdated = getFolderLastUpdated(f);
      const key = getItemKey({ ...f, itemType: "folder" });
      return (
        <tr
          key={`folder-${f.id}`}
          className={`folder-row ${
            selectedItems.has(key) ? "row-selected" : ""
          }`}
        >
          {console.log(folders, "folder from here")}
          <td style={{ width: "40px", textAlign: "center" }}>
            <input
              type="checkbox"
              checked={selectedItems.has(key)}
              className="icon-image"
              onChange={(e) => {
                e.stopPropagation();
                toggleSelectFolder(f);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </td>
          <td
            onClick={() => navigateInto(f.name)}
            style={{ cursor: "pointer" }}
          >
            <FolderIcon size={20} strokeWidth={1.7} className="table-icon" />
            &nbsp;&nbsp;{f.name}
          </td>
          <td>Folder</td>
          <td>{formatSize(getFolderSize(f))}</td>
          <td>{f.uploadedBy || "Admin"}</td>
          {/* <td>{lastUpdated ? lastUpdated.toLocaleString() : "-"}</td> */}
          <td>
            {formatDateToUserTimeZone(lastUpdated)
              ? formatDateToUserTimeZone(lastUpdated)
              : "-"}
          </td>
          <td>
            <button
              className="icon-button"
              onClick={(e) => {
                e.stopPropagation();
                openRenameModal({ ...f, itemType: "folder" });
              }}
              title="Rename"
            >
              <img src={renameIcon} alt="Rename" className="profile-i" />
            </button>
            {/* <button
              className="icon-button"
              onClick={(e) => {
                e.stopPropagation();
                onRowDelete({ ...f, itemType: "folder" }); // open delete modal for single item
              }}
              title="Delete"
            >
              <img src={deleteIcon} alt="Delete" className="profile-i" />
            </button> */}
            <button
              className="icon-button"
              onClick={(e) => {
                e.stopPropagation();
                // select only this folder and open move modal
                setSelectedItems(new Set([key]));
                openMoveUI();
              }}
              title="Move Folder"
            >
              <FolderSymlink
                size={20}
                strokeWidth={1.6}
                className="profile-i"
                style={{ color: "var(--primary-color)" }}
              />
            </button>
          </td>
        </tr>
      );
    });

    const fileRows = files.map((f) => {
      const displayKey = getItemKey({ ...f, itemType: f.itemType || "file" });
      return (
        <tr
          // key={`${f.itemType}-${f.id ?? f.uploaded_file_id}`}
          key={getItemKey(f)}
          className={`folder-row ${
            selectedItems.has(displayKey) ? "row-selected" : ""
          }`}
        >
          {console.log(files, "files from here")}
          <td style={{ width: "40px", textAlign: "center" }}>
            <input
              type="checkbox"
              className="icon-image"
              // checked={selectedItems.has(displayKey)}
              checked={selectedItems.has(getItemKey(f))}
              onChange={(e) => {
                e.stopPropagation();
                toggleSelectFile(f);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </td>
          <td onClick={() => toggleSelectFile(f)} style={{ cursor: "pointer" }}>
            <FileIcon size={20} strokeWidth={1.7} className="table-icon" />
            &nbsp;&nbsp;
            <span
              className="text-truncate"
              style={{
                // maxWidth: "300px",
                // display: "inline-block",
                verticalAlign: "middle",
                cursor: getFileCategory(f).includes("URL")
                  ? "pointer"
                  : "default",
              }}
            >
              {renderWithLinks(f.name)}
            </span>
          </td>
          <td>{getFileCategory(f)}</td>
          <td>{formatSize(f.size)}</td>
          <td className="text-left">{f.uploadedBy ? f.uploadedBy : "N/A"}</td>
          <td>
            {formatDateToUserTimeZone(f.updatedAt)
              ? formatDateToUserTimeZone(f.updatedAt)
              : "-"}
          </td>
          <td>
            <button
              className="icon-button"
              disabled={isUrlType(f)}
              onClick={(e) => {
                if (isUrlType(f)) return;
                e.stopPropagation();
                handleDownload(f);
              }}
              title={
                isUrlType(f) ? "Download disabled for URL files" : "Download"
              }
              style={{
                opacity: isUrlType(f) ? 0.4 : 1,
                cursor: isUrlType(f) ? "not-allowed" : "pointer",
              }}
            >
              <img
                src={downloadIcon}
                alt="Download File"
                className="profile-i"
              />
            </button>

            {/* <button
              className="icon-button"
              onClick={(e) => {
                e.stopPropagation();
                onRowDelete({ ...f, itemType: f.itemType || "file" });
              }}
              title="Delete"
            >
              <img src={deleteIcon} alt="Delete" className="profile-i" />
            </button> */}

            <button
              className="icon-button"
              onClick={(e) => {
                e.stopPropagation();
                // select only this file & open move modal
                setSelectedItems(new Set([displayKey]));
                openMoveUI();
              }}
              title="Move File"
            >
              <FileSymlink
                size={20}
                strokeWidth={1.6}
                className="profile-i"
                style={{ color: "var(--primary-color)" }}
              />
            </button>
          </td>
        </tr>
      );
    });

    return [...folderRows, ...fileRows];
  };

  // getFolder last updated recursive
  function getFolderLastUpdated(folder) {
    let timestamps = [];
    if (folder.__files && folder.__files.length) {
      timestamps.push(
        ...folder.__files.map((f) => f.updatedAt).filter(Boolean)
      );
    }
    Object.values(folder.__folders || {}).forEach((sub) => {
      const subLast = getFolderLastUpdated(sub);
      if (subLast) timestamps.push(subLast);
    });
    if (!timestamps.length) return null;
    return new Date(Math.max(...timestamps.map((t) => new Date(t).getTime())));
  }

  // breadcrumb helpers
  const getDisplayBreadcrumbWithMapping = (fullPath) => {
    if (!fullPath || fullPath.length <= 3) {
      return { display: fullPath, mapping: fullPath.map((_, idx) => idx) };
    }
    const first = fullPath[0];
    const secondLast = fullPath[fullPath.length - 2];
    const last = fullPath[fullPath.length - 1];
    const display = [first, "...", secondLast, last];
    const mapping = [0, -1, fullPath.length - 2, fullPath.length - 1];
    return { display, mapping };
  };

  const handleCrumbClick = (displayIndex) => {
    const { mapping } = getDisplayBreadcrumbWithMapping(path);
    const actualIndex = mapping[displayIndex];
    if (actualIndex === 0) return;
    setPath((prev) => prev.slice(0, actualIndex + 1));
  };

  // handle confirmation modal delete action
  const handleConfirmDelete = async () => {
    // If deleteTarget is a single item with id and itemType, delete that single item.
    // Otherwise (deleteTarget is synthetic like { name: "2 items" }), delete selectedItems.
    try {
      setIsDeleting(true);
      if (deleteTarget && deleteTarget.id) {
        // single item delete
        if (deleteTarget.itemType === "folder") {
          // folder delete endpoint (used in your code earlier)
          await api.delete(
            `${process.env.REACT_APP_API_URL}/trained-documents/${deleteTarget.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } else if (deleteTarget.itemType === "uploaded") {
          await api.delete(
            `${process.env.REACT_APP_API_URL}/trained-documents/uploaded-file/${deleteTarget.uploaded_file_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else {
          // file (trained)
          await api.delete(
            `${process.env.REACT_APP_API_URL}/trained-documents/delete-items`,
            {
              headers: { Authorization: `Bearer ${token}` },
              data: { item_ids: [deleteTarget.id] },
            }
          );
        }
        showSuccessToast("Deleted successfully");
      } else {
        // multi-delete using selectedItems
        await doDeleteItems();
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await fetchData();
    } catch (err) {
      console.error("❌ handleConfirmDelete error", err);
      showErrorToast("Error deleting item(s)");
    } finally {
      setIsDeleting(false);
    }
  };

  // UI: grid rendering uses same selection helpers
  return (
    <div className="knowledge-space-container">
      <div className="page-columns">
        <div className="repo-main-content">
          <div className="header">
            <h2>
              <img src={Repo_Folder} alt="repo" className="profile-i" />
              <KnowledgeTitleBar
                breadcrumbPath={getDisplayBreadcrumbWithMapping(path).display}
                onCrumbClick={handleCrumbClick}
              />
            </h2>

            <div className="header-right">
              <div
                className="input-group custom-search-group"
                style={{ width: 320 }}
              >
                <span className="input-group-text">
                  <i className="bi bi-search" />
                </span>
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="view-toggle">
                <button
                  className={`toggle-btn ${
                    viewMode === "list" ? "active" : ""
                  }`}
                  onClick={() => setViewMode("list")}
                >
                  <img src={ListIcon} alt="list" className="toggle-icon" />
                </button>
                <button
                  className={`toggle-btn ${
                    viewMode === "grid" ? "active" : ""
                  }`}
                  onClick={() => setViewMode("grid")}
                >
                  <img src={GridIcon} alt="grid" className="toggle-icon" />
                </button>
              </div>

              {showNewButton && (
                <button
                  className="new-btn"
                  onClick={() => setShowFolderModal(true)}
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <Plus size={16} /> New
                </button>
              )}
            </div>
          </div>

          {/* TOP ACTION BAR (multi-select) */}
          {selectedItems.size > 0 && (
            <div className="action-toolbar text-dark">
              <span>
                <strong>{selectedItems.size}</strong> selected
              </span>

              {/* <button className="btn btn-sm btn-danger" onClick={onTopDelete}>
                Delete Selected
              </button> */}

              <button
                className="btn btn-sm btn-dark"
                onClick={() => {
                  openMoveUI();
                }}
              >
                Move Selected
              </button>

              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setSelectedItems(new Set());
                  setSelectAll(false);
                }}
              >
                Clear Selection
              </button>
            </div>
          )}

          <div className="folder-section">
            {viewMode === "list" && (
              <div className="table-wrapper1">
                <table className="folder-table ">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>
                        <input
                          type="checkbox"
                          className="icon-image"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th style={{ paddingLeft: 20 }}>Name</th>
                      <th>Type</th>
                      <th>Size</th>
                      <th>Uploaded By</th>
                      <th>Last Updated On</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderFolderAndFileRows(
                      filteredNode || { __folders: {}, __files: [] }
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {viewMode === "grid" && (
              <div className="grid-wrapper">
                <div className="grid-container">
                  {[
                    ...Object.values(filteredNode?.__folders || {}),
                    ...(filteredNode?.__files || []),
                  ].map((item) => {
                    const key = getItemKey(item);
                    const isSelected = selectedItems.has(key);

                    return (
                      <div
                        key={key}
                        className="grid-item"
                        style={{
                          borderColor: isSelected
                            ? "var(--primary-color)"
                            : undefined,
                        }}
                      >
                        <div
                          className="grid-card overflow-hidden"
                          onClick={() => {
                            if (item.itemType === "folder") {
                              navigateInto(item.name);
                            } else {
                              toggleSelectFile(item);
                            }
                          }}
                        >
                          {item.itemType === "folder" ? (
                            <FolderIcon
                              size={26}
                              strokeWidth={1.8}
                              className="grid-icon"
                            />
                          ) : (
                            <FileIcon
                              size={26}
                              strokeWidth={1.8}
                              className="grid-icon"
                            />
                          )}

                          <div className="grid-name text-truncate">
                            {item.name}
                          </div>
                        </div>

                        <div className="grid-actions">
                          <button
                            onClick={() => openRenameModal(item)}
                            title="Rename"
                            className="icon-button"
                          >
                            <img
                              src={renameIcon}
                              alt="Rename"
                              className="profile-i"
                            />
                          </button>

                          {/* <button
                            onClick={() => {
                              setDeleteTarget(item);
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                            className="icon-button"
                          >
                            <img
                              src={deleteIcon}
                              alt="Delete"
                              className="profile-i"
                            />
                          </button> */}

                          <button
                            className="icon-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedItems(new Set([key]));
                              openMoveUI();
                            }}
                            title="Move"
                          >
                            <FolderSymlink
                              size={18}
                              strokeWidth={1.6}
                              className="profile-i"
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Create Folder Modal */}
          {showFolderModal && (
            <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
              <div className="modal-history modal-dialog-centered history-model">
                <div className="modal-content position-relative">
                  <button
                    type="button"
                    className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                    onClick={() => {
                      if (!isCreatingFolder) {
                        setShowFolderModal(false);
                        setNewFolderName("");
                      }
                    }}
                    disabled={isCreatingFolder}
                  >
                    <img
                      src={crossIcon}
                      className="icon-image cross-i"
                      alt="Close"
                    />
                  </button>

                  <div className="text-center p-3">
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "var(--icon-bg)",
                      }}
                    >
                      <FolderIcon
                        size={40}
                        strokeWidth={1.7}
                        className="table-icon profile-i"
                      />
                    </div>

                    <input
                      type="text"
                      className="form-control text-center mt-4"
                      placeholder="Enter folder name"
                      value={newFolderName}
                      disabled={isCreatingFolder}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="modal-footer justify-content-center gap-2">
                    <button
                      type="button"
                      className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                      disabled={isCreatingFolder}
                      onClick={() => {
                        if (!isCreatingFolder) {
                          setShowFolderModal(false);
                          setNewFolderName("");
                        }
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                      disabled={!newFolderName.trim() || isCreatingFolder}
                      onClick={() =>
                        createFolder(
                          newFolderName.trim(),
                          activeNode?.id === "root" ? null : activeNode?.id
                        )
                      }
                    >
                      {isCreatingFolder ? "Creating..." : "Create"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Move Modal */}
          {showMoveUI && tree && (
            <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
              <div className="modal-history modal-dialog-centered history-model">
                <div className="modal-content position-relative">
                  <button
                    type="button"
                    className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                    onClick={() => setShowMoveUI(false)}
                  >
                    <img
                      src={crossIcon}
                      className="icon-image cross-i"
                      alt="Close"
                    />
                  </button>

                  <div className="text-center p-3">
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "var(--icon-bg)",
                      }}
                    >
                      <FolderSymlink
                        size={38}
                        strokeWidth={1.6}
                        className="profile-i"
                        style={{ color: "var(--primary-color)" }}
                      />
                    </div>
                  </div>

                  <div
                    className="modal-body"
                    style={{ padding: 20, maxHeight: 350, overflowY: "auto" }}
                  >
                    <DndProvider backend={HTML5Backend}>
                      <FolderNode
                        node={tree}
                        level={0}
                        moveTargetId={moveTargetId}
                        setMoveTargetId={setMoveTargetId}
                        renameFolderId={renameFolderId}
                        setRenameFolderId={setRenameFolderId}
                        onRename={renameFolder}
                      />
                    </DndProvider>
                  </div>

                  <div className="modal-footer justify-content-center gap-2">
                    <button
                      type="button"
                      className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                      onClick={handleNewFolderInMove}
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <Plus size={16} /> New Folder
                    </button>

                    <button
                      type="button"
                      className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                      onClick={() => setShowMoveUI(false)}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                      onClick={performMove}
                      disabled={!selectedItems.size}
                    >
                      Move Here
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rename Modal */}
          {showRenameModal && renameTarget && (
            <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
              <div className="modal-history modal-dialog-centered history-model">
                <div className="modal-content position-relative">
                  <button
                    type="button"
                    className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                    onClick={() => {
                      if (!isRenaming) {
                        setShowRenameModal(false);
                        setRenameTarget(null);
                        setNewName("");
                      }
                    }}
                  >
                    <img
                      src={crossIcon}
                      className="icon-image cross-i"
                      alt="Close"
                    />
                  </button>

                  <div className="text-center p-3">
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "var(--icon-bg)",
                      }}
                    >
                      <img
                        src={renameIcon}
                        style={{ width: "30px" }}
                        alt="Rename"
                        className="profile-i"
                      />
                    </div>

                    <input
                      type="text"
                      className="form-control text-center mt-4"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      disabled={isRenaming}
                      autoFocus
                    />
                  </div>

                  <div className="modal-footer justify-content-center gap-2">
                    <button
                      type="button"
                      disabled={isRenaming}
                      className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                      onClick={() => {
                        if (!isRenaming) {
                          setShowRenameModal(false);
                          setRenameTarget(null);
                          setNewName("");
                        }
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      disabled={!newName.trim() || isRenaming}
                      className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                      onClick={performRename}
                    >
                      {isRenaming ? "Renaming..." : "Rename"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && deleteTarget && (
            <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
              <div className="modal-history modal-dialog-centered history-model">
                <div className="modal-content position-relative">
                  <button
                    type="button"
                    className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                    onClick={() => {
                      if (!isDeleting) {
                        setShowDeleteModal(false);
                        setDeleteTarget(null);
                      }
                    }}
                    aria-label="Close"
                  >
                    <img
                      src={crossIcon}
                      className="icon-image cross-i"
                      alt="Close"
                    />
                  </button>

                  <div className="text-center p-3">
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "var(--icon-bg)",
                      }}
                    >
                      <img
                        src={deleteIcon}
                        alt="Delete"
                        className="profile-i"
                        style={{ width: "40px", height: "40px" }}
                      />
                    </div>

                    <p className="fw-bold mt-3 mb-3 refresh-prompts text-wrap">
                      {deleteTarget.id ? (
                        <>
                          Are you sure you want to permanently delete{" "}
                          <strong>{deleteTarget.name}</strong>?
                          {deleteTarget.itemType === "folder" &&
                            " This will also delete all its contents."}
                        </>
                      ) : (
                        <>
                          Are you sure you want to permanently delete{" "}
                          <strong>
                            {Array.from(selectedItems).length} item(s)
                          </strong>
                          ?
                        </>
                      )}
                    </p>
                  </div>

                  <div className="modal-footer justify-content-center gap-2">
                    <button
                      type="button"
                      disabled={isDeleting}
                      className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                      onClick={() => {
                        if (!isDeleting) {
                          setShowDeleteModal(false);
                          setDeleteTarget(null);
                        }
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      disabled={isDeleting}
                      className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                      onClick={handleConfirmDelete}
                    >
                      {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepositorySpace;
